"use client";

import React, { useState, useEffect, useRef } from "react";
import { get_comments, post_comments, get_course_progress, save_video_progress, getPdfProxyUrl } from "@/api";
import toast from "react-hot-toast";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import "./coursePage.css";
import dynamic from "next/dynamic";
const VideoJsPlayer = dynamic(() => import("../VideoPlayer/Video"), { ssr: false });
import CommentEditor from "../CommentBox/CommentEditor";
import { CaretDoubleUp, CaretDown, CaretUp, X } from "@phosphor-icons/react";

const getCourseId = (course) =>
  course?.id || course?._id || course?.course_id || course?.slug || "";

const CoursePlayer = ({ course }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [selectedPDFTitle, setSelectedPDFTitle] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [comments, setComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [selectedVideoTitle, setSelectedVideoTitle] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  // New progress tracking state
  const [progressMap, setProgressMap] = useState({});
  const [overallProgress, setOverallProgress] = useState(null);
  const commentRequestId = useRef(0);
  const progressSaveIds = useRef({});

  const courseId = getCourseId(course);

  const fetchProgress = async () => {
    if (!courseId) return;
    try {
      const data = await get_course_progress(courseId);
      if (data) {
        setProgressMap(data.video_progress || {});
        setOverallProgress(data.overall_progress_percentage);
      }
    } catch (error) {
      console.error("Failed to fetch course progress:", error);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [courseId]);

  useEffect(() => {
    if (course?.subtitles?.length && courseId) {
      for (let subtitle of course.subtitles) {
        if (subtitle.videos && subtitle.videos.length > 0) {
          const firstVideo = subtitle.videos[0];
          setSelectedVideo(firstVideo.path);
          setSelectedVideoTitle(firstVideo.title);
          setSelectedTopic(subtitle.title);
          setSelectedPDF(null);
          setExpandedTopics((previous) => ({ ...previous, [subtitle.title]: true }));
          fetchComments(courseId, subtitle.title, firstVideo.title);
          break;
        }
      }
    }
  }, [course, courseId]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const toggleTopic = (topic) => {
    setExpandedTopics((prev) => ({
      ...prev,
      [topic]: !prev[topic],
    }));
  };

  const handleVideoSelect = (video, topic) => {
    if (!video?.path || !video?.title) {
      toast.error("This lesson is not available yet.");
      return;
    }
    setSelectedVideo(video.path);
    setSelectedVideoTitle(video.title);
    setSelectedTopic(topic);
    fetchComments(courseId, topic, video.title);
    toggleSidebar();
  };

  const handlePDFSelect = (pdf, topic) => {
    // Documents are stored as { title, path } — the field is "path", not "pdf_url".
    // Accept pdf_url as a fallback for any legacy entries.
    const rawUrl = pdf?.path || pdf?.pdf_url;
    if (!rawUrl) {
      toast.error("This document is not available yet.");
      return;
    }
    // Route the PDF through the backend proxy so the CDN's X-Frame-Options header
    // does not block it from being embedded in the <iframe>.
    const proxyUrl = getPdfProxyUrl(courseId, rawUrl);
    setSelectedPDF(proxyUrl);
    setSelectedPDFTitle(pdf.title);
    setSelectedVideo(null);
    setComments([]);
    toggleSidebar();
  };

  const fetchComments = async (courseId, topic, videoTitle) => {
    const requestId = ++commentRequestId.current;
    try {
      setIsLoadingComments(true);
      const data = await get_comments(courseId, videoTitle);
      if (requestId === commentRequestId.current) setComments(data.comments || []);
    } catch (error) {
      if (requestId === commentRequestId.current) toast.error("Failed to fetch comments");
    } finally {
      if (requestId === commentRequestId.current) setIsLoadingComments(false);
    }
  };

  const postComment = async (text) => {
    if (!selectedVideo || !selectedVideoTitle || !selectedTopic) return;
    try {
      const response = await post_comments(
        courseId,
        selectedTopic,
        selectedVideoTitle,
        text
      );
      toast.success("Comment posted successfully!");
      fetchComments(courseId, selectedTopic, selectedVideoTitle);
    } catch (error) {
      toast.error("Failed to post comment");
    }
  };

  const handleTimeUpdate = async (topic, videoTitle, currentTime, duration) => {
    if (!courseId || !topic || !videoTitle || !Number.isFinite(duration) || duration <= 0) return;
    const safeTime = Math.max(0, Math.min(currentTime || 0, duration));
    const videoKey = `${topic}|${videoTitle}`;
    const requestId = (progressSaveIds.current[videoKey] || 0) + 1;
    progressSaveIds.current[videoKey] = requestId;
    try {
      const res = await save_video_progress(
        courseId,
        topic,
        videoTitle,
        safeTime,
        duration
      );

      // Ignore stale responses so delayed saves cannot move progress backwards.
      if (progressSaveIds.current[videoKey] !== requestId) return;
      const currentVideoProgress = progressMap[videoKey];

      // Update local progress state
      setProgressMap((prev) => ({
        ...prev,
        [videoKey]: {
          position_seconds: safeTime,
          duration_seconds: duration,
          completed: res?.completed || (prev[videoKey]?.completed)
        }
      }));

      // Refresh overall completion if the status just changed to complete
      if (res?.completed && !currentVideoProgress?.completed) {
        fetchProgress();
      }
    } catch (error) {
      console.error("Failed to save video progress:", error);
    }
  };

  const markVideoAsSeen = async () => {
    if (!courseId || !selectedTopic || !selectedVideoTitle) return;
    const videoKey = `${selectedTopic}|${selectedVideoTitle}`;
    progressSaveIds.current[videoKey] = (progressSaveIds.current[videoKey] || 0) + 1;
    const currentProgress = progressMap[videoKey];
    const duration = currentProgress?.duration_seconds || 100;
    try {
      const res = await save_video_progress(
        courseId,
        selectedTopic,
        selectedVideoTitle,
        duration,
        duration
      );
      setProgressMap((prev) => ({
        ...prev,
        [videoKey]: {
          position_seconds: duration,
          duration_seconds: duration,
          completed: true
        }
      }));
      toast.success("Video marked as completed!");
      fetchProgress();
    } catch (error) {
      toast.error("Failed to mark video as completed");
      console.error(error);
    }
  };

  const handlePlayerReady = (player) => {
    // Optional: player setup callback
  };

  return (
    <>
      <div className="course-page">
        <div className={`course-sidebar ${isOpen ? "open" : ""}`}>
          <div>
            <h3 className="sidebar-title flex flex-col justify-between items-stretch">
              <div className="flex justify-between items-center w-full">
                Course Topics
                <span>
                  <button onClick={toggleSidebar} className="flex hidden-class">
                    <X size={24} />
                  </button>
                </span>
              </div>
            </h3>
          </div>
          {(course?.subtitles || []).map((subtitle, index) => (
            <div key={index} className="sidebar-section">
              <h4 onClick={() => toggleTopic(subtitle.title)}>
                <p>{subtitle.title}</p>
                <p>
                  {expandedTopics[subtitle.title] ? (
                    <CaretUp size={24} />
                  ) : (
                    <CaretDown size={24} />
                  )}
                </p>
              </h4>
              {expandedTopics[subtitle.title] && (
                <ul>
                  {(subtitle.videos || []).map((video, vIndex) => {
                    const videoKey = `${subtitle.title}|${video.title}`;
                    const progress = progressMap[videoKey];
                    const isCompleted = progress?.completed;

                    return (
                      <li key={`video-${vIndex}`}>
                        <button
                          className={`video-button flex items-center justify-between w-full ${selectedTopic === subtitle.title && selectedVideoTitle === video.title ? "active" : ""}`}
                          onClick={() => handleVideoSelect(video, subtitle.title)}
                        >
                          <span className="truncate flex items-center gap-1.5">
                            {isCompleted ? "✅" : "🎬"} {video.title}
                          </span>
                          {progress && !isCompleted && progress.position_seconds > 0 && (
                            <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium ml-1 flex-shrink-0">
                              {Math.round((progress.position_seconds / progress.duration_seconds) * 100)}%
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                  {subtitle.documents?.map((pdf, dIndex) => (
                    <li key={`pdf-${dIndex}`}>
                      <button
                        className="pdf-button"
                        onClick={() => handlePDFSelect(pdf, subtitle.title)}
                      >
                        📄 {pdf.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className={`course-content ${isOpen ? "shifted" : ""}`}>
          <h1 className="text-lg lg:text-[25px] font-bold text-black/90 p-2">
            {(course?.title || "Course").replace(/"/g, "")}
          </h1>

          <div className="media-container">
            {selectedVideo && (
              <VideoJsPlayer
                key={`${selectedTopic}|${selectedVideoTitle}|${selectedVideo}`}
                source={selectedVideo}
                initialTime={progressMap[`${selectedTopic}|${selectedVideoTitle}`]?.position_seconds || 0}
                onTimeUpdate={(currentTime, duration) => handleTimeUpdate(selectedTopic, selectedVideoTitle, currentTime, duration)}
                onReady={handlePlayerReady}
              />
            )}
          </div>

          {selectedVideo && (
            <div className="mt-4 px-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-xl lg:text-2xl leading-[110%] font-semibold text-gray-800">
                {selectedVideoTitle}
              </h2>
              <button
                onClick={markVideoAsSeen}
                disabled={progressMap[`${selectedTopic}|${selectedVideoTitle}`]?.completed}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer
                  ${
                    progressMap[`${selectedTopic}|${selectedVideoTitle}`]?.completed
                      ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 cursor-default"
                      : "bg-[#0D6FEA] hover:bg-blue-600 text-white hover:scale-[1.02] active:scale-98"
                  }`}
              >
                {progressMap[`${selectedTopic}|${selectedVideoTitle}`]?.completed ? (
                  <>✓ Completed</>
                ) : (
                  <>Mark as Seen</>
                )}
              </button>
            </div>
          )}

          {selectedPDF && (
            <div
              className="pdf-viewer"
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.ctrlKey && (e.key === "p" || e.key === "s")) {
                  e.preventDefault();
                }
              }}
            >
              <iframe
                src={selectedPDF}
                title={selectedPDFTitle || "PDF Document"}
              />
            </div>
          )}

          {selectedVideo && (
            <div className="comment-section">
              <h3>Comments</h3>
              {isLoadingComments ? (
                <p>Loading comments...</p>
              ) : comments.length > 0 ? (
                <ul className="comments-list">
                  {comments.map((comment, index) => (
                    <li key={index} className="comment">
                      <div className="comment-header">
                        <strong>{comment.user_name}</strong>
                        <span className="comment-date">
                          {new Date(comment.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="comment-content">
                        {parse(
                          DOMPurify.sanitize(comment.text || "", {
                            ADD_TAGS: ["img"],
                            ADD_ATTR: ["src", "alt"],
                            ALLOWED_URI_REGEXP:
                              /^data:image\/(png|jpg|jpeg|gif|webp);base64/,
                          })
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No comments yet.</p>
              )}
              <CommentEditor onSubmit={postComment} />
            </div>
          )}
        </div>
      </div>
      <button
        className="flex flex-col hidden-class justify-center items-center w-full bottom-0 bg-white shadow-2xl rounded-md z-5 fixed"
        onClick={toggleSidebar}
      >
        <CaretDoubleUp size={28} />
        <p className="text-[20px]">Course Content</p>
      </button>
    </>
  );
};

export default CoursePlayer;
